import React from 'react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core';
import MuiSvgIcon from '@material-ui/core/SvgIcon';

import { units } from '@ha/dsl';

type MuiSvgIconProps = import('@material-ui/core/SvgIcon').SvgIconProps;

type CustomColor = 'mint' | 'default' | 'lightgrey' | 'grey' | 'silver';
const CUSTOM_COLORS: CustomColor[] = [
  'mint',
  'default',
  'lightgrey',
  'silver',
  'grey',
];

const isCustomColor = (color?: string): color is CustomColor =>
  typeof color === 'string' && CUSTOM_COLORS.some(custom => custom === color);

export type SvgIconProps = Omit<MuiSvgIconProps, 'color' | 'fontSize'> & {
  color?: MuiSvgIconProps['color'] | CustomColor;
  fontSize?: MuiSvgIconProps['fontSize'] | 'sm' | 'xs';
};

const useStyles = makeStyles(theme => ({
  'color-mint': {
    color: theme.palette.secondary.main,
  },
  'color-default': {
    color: theme.palette.grey[700],
  },
  'color-grey': {
    color: theme.palette.grey[500],
  },
  'color-lightgrey': {
    color: theme.palette.grey[400],
  },
  'color-silver': {
    color: theme.palette.grey[100],
  },
  'fontSize-xs': {
    fontSize: units.rem(0.5),
  },
  'fontSize-sm': {
    fontSize: units.rem(1),
  },
}));

const SvgIcon = React.forwardRef<SVGSVGElement, SvgIconProps>(
  ({ color, fontSize, className, ...rest }, ref) => {
    const classes = useStyles();

    return (
      <MuiSvgIcon
        {...rest}
        className={cx(
          className,
          classes[`color-${color}`],
          classes[`fontSize-${fontSize}`],
        )}
        color={isCustomColor(color) ? undefined : color}
        fontSize={fontSize !== 'xs' && fontSize !== 'sm' ? fontSize : undefined}
        ref={ref as React.RefObject<SVGSVGElement>}
      />
    );
  },
);

export default React.memo(SvgIcon);
